import * as React from 'react';

import {
  resolveScrollElement,
  ScrollContainerTarget
} from '../utils/scrollContainerTarget';

import { bindScrollController } from './horizontalScroll/bindScrollController';
import { ScrollZone } from './horizontalScroll/types';
import type { ScrollController } from './horizontalScroll/types';

/** Max rAF retries while waiting for scroll container ref to mount */
const MAX_ATTACH_ATTEMPTS = 10;

export type UseHorizontalScrollAttachOptions = {
  target: ScrollContainerTarget;
  enabled: boolean;
  wheelScroll: boolean;
  arrows: boolean;
  zone: ScrollZone;
  scrollbarZoneHeight: number;
  smoothFactor: number;
  controllerRef: React.MutableRefObject<ScrollController | null>;
  onScroll: () => void;
};

const useHorizontalScrollAttach = ({
  target,
  enabled,
  wheelScroll,
  arrows,
  zone,
  scrollbarZoneHeight,
  smoothFactor,
  controllerRef,
  onScroll
}: UseHorizontalScrollAttachOptions): void => {
  React.useEffect(() => {
    if (!enabled) {
      controllerRef.current = null;
      return;
    }

    let frameId: number | undefined;
    let cleanup: (() => void) | undefined;
    let attachAttempts = 0;

    const attach = () => {
      const container = resolveScrollElement(target);
      if (!container) {
        attachAttempts += 1;
        if (attachAttempts < MAX_ATTACH_ATTEMPTS) {
          frameId = requestAnimationFrame(attach);
        }
        return;
      }

      const {
        controller,
        cancelAnimation,
        syncTargetFromContainer,
        handleWheel
      } = bindScrollController(container, {
        smoothFactor,
        wheelScroll,
        zone,
        scrollbarZoneHeight
      });

      controllerRef.current = controller;

      const handleScroll = () => {
        syncTargetFromContainer();
        if (arrows) {
          onScroll();
        }
      };

      container.addEventListener('scroll', handleScroll, { passive: true });

      if (wheelScroll) {
        container.addEventListener('wheel', handleWheel, { passive: false });
      }

      if (arrows) {
        onScroll();
      }

      cleanup = () => {
        container.removeEventListener('scroll', handleScroll);
        if (wheelScroll) {
          container.removeEventListener('wheel', handleWheel);
        }
        cancelAnimation();
        controllerRef.current = null;
      };
    };

    attach();

    return () => {
      if (frameId !== undefined) {
        cancelAnimationFrame(frameId);
      }
      cleanup?.();
    };
  }, [
    enabled,
    wheelScroll,
    arrows,
    zone,
    scrollbarZoneHeight,
    smoothFactor,
    target,
    controllerRef,
    onScroll
  ]);
};

export default useHorizontalScrollAttach;
